import { useInfiniteQuery } from 'react-query';

import { useOAIQuery } from '~/hooks/reactQuery';
import { SHOPPING_CART } from '~/hooks/useCartTypeParam';
import { fetchOAIData } from '~/libs/openapi';
import type { OAIApiError } from '~/libs/openApiError';
import type { OAIParameters, OAIResponse, OAISchema } from '~/types/openapi';
import type { UseOAIQueryProps } from '~/types/reactQuery';

import { getSnowdonJwt } from '../auth/utils';
import { MAX_SHOWCASE_ITEM_SIZE } from '../showcase/constants';

export const useCashnotePlusShowcase = (
  code: string,
  variables: Record<string, unknown> = {},
) => {
  const query = useInfiniteQuery(
    ['/api/market-place/v4/showcases/cashnote-plus', code],
    async ({ signal, pageParam }) => {
      const data = await fetchOAIData<
        OAISchema['MarketShowcaseProductsResponseV3']
      >({
        queryKey: '/api/market-place/v4/showcases/cashnote-plus',
        variables: {
          code,
          ...((pageParam || {}) as Record<string, unknown>),
          ...variables,
        },
        fetchOptions: {
          signal,
          headers: {
            'X-Client-Auth-Token': getSnowdonJwt(),
          },
        },
      });

      return data.products;
    },
    {
      keepPreviousData: true,
      getNextPageParam: (params) => {
        const { hasNextPage, next } = params as unknown as {
          content: OAISchema['MarketShowcaseProductsResponseV3'][];
          hasNextPage: boolean;
          next: number;
          size: number;
        };
        if (!hasNextPage) {
          return undefined;
        }

        return { next };
      },
    },
  );

  const fetchNextPage = () => {
    if (query.isFetching || !query.hasNextPage) {
      return;
    }

    void query.fetchNextPage();
  };

  const pages = query.data?.pages.reduce(
    (current, next) => current.concat(next!.content),
    [] as OAISchema['MarketSelectProductResponseV2'][],
  );

  return {
    ...query,
    pages,
    fetchNextPage,
  };
};

export const useCashnotePlusShowcaseAll = (code: string) => {
  return useOAIQuery({
    queryKey: '/api/market-place/v4/showcases/cashnote-plus',
    variables: {
      code,
      size: MAX_SHOWCASE_ITEM_SIZE,
    },
    queryOptions: {
      keepPreviousData: true,
    },
  });
};

export const usePlusMembershipEvent = () => {
  return useOAIQuery({
    queryKey: '/api/market-place/v1/event/plus-membership-discount',
  });
};

export const usePlusMembershipCouponSearch = () => {
  return useOAIQuery({
    queryKey: '/api/market-place/v1/buyer/plus-membership/current',
  });
};

/**
 * @description 상품 - 플러스멤버십 할인 예상 가격과 사유
 * */
export const useProductDetailsNudge = (offerId: number) => {
  return useOAIQuery({
    queryKey: '/api/market-place/v2/products/{offerId}/details/nudge',
    variables: { offerId },
  });
};

/**
 * @description 장바구니, 주문결제 - 플러스멤버십 가입시 장바구니에 선택된 상품들의 총 할인가격
 * */
export const useMembershipRegisteredExpectedAmountNudge = (
  variables: OAIParameters<
    '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment/nudge',
    'get'
  > = { cartType: SHOPPING_CART },
  queryOptions: UseOAIQueryProps<
    '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment/nudge',
    typeof variables,
    OAIResponse<
      '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment/nudge',
      'get'
    >,
    OAIApiError
  >['queryOptions'] = {},
) => {
  return useOAIQuery({
    queryKey:
      '/api/market-place/v1/shopping-cart-items/total-in-shopping-cart-payment/nudge',
    variables,
    queryOptions,
  });
};
